import { Auth } from '@aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import { gql } from '@urql/core'
import { setupDevtoolsPlugin } from '@vue/devtools-api'
import { computed, ref, shallowRef, reactive } from '@vue/runtime-core'
/**
 *
 */
class CMSAuthManager {
  /**
   *
   */
  constructor () {
    Hub.listen('auth', this._onAuthEvent.bind(this))

    this.errors = [
      {
        name: 'password_uppercase',
        test: (message) => /Password must have uppercase characters/.test(message),
        message: () => ('Password must contain at least one uppercase character')
      },
      {
        name: 'password_lowercase',
        test: (message) => /Password must have lowercase characters/.test(message),
        message: () => ('Password must contain at least one lowercase character')
      },
      {
        name: 'password_length',
        test: (message) => /Password not long enough/.test(message),
        message: () => ('Password must be at least 8 characters long')
      },
      {
        name: 'password_number',
        test: (message) => /Password must have numeric characters/.test(message),
        message: () => ('Password must contain at least one number')
      },
      {
        name: 'invalid_email',
        test: (message) => /(Invalid email address format|Value at 'username' failed to satisfy constraint)/.test(message),
        message: () => ('Email must be valid')
      },
      {
        name: 'login_failed',
        test: (message) => /UserNotFoundException/.test(message), // happens on code verification and login
        message: () => ('Email not found')
      },
      {
        name: 'code_mismatch',
        test: (message) => /CodeMismatchException/.test(message), // happens on code verification and login
        message: () => ('The code you entered is not valid.')
      },
      {
        name: 'login_failed_1',
        test: (message) => /NotAuthorizedException/.test(message),
        message: () => ('Login information incorrect')
      },
      {
        name: 'signup_existing_user',
        test: (message) => /(UsernameExistsException|PreSignUp\+failed\+with\+error\+E-mail\+Already\+exists|PreSignUp failed with error E-mail Already exists)/.test(message),
        message: () => ('User already registered')
      },
      {
        name: 'forgot_password_limit',
        test: (message) => /Attempt limit exceeded, please try after some time./.test(message),
        message: () => ('Password reset attempt limit exceeded, please retry after some time.')
      },
      {
        name: 'change_password_session_expired', // when user tries to change passwords more than once
        test: (message) => /Invalid session for the user, session can only be used once./.test(message),
        message: () => ('This invitation link has expired.')
      },
      {
        name: 'reset_password_code_expired', // when user tries to change passwords more than once
        test: (message) => /(ExpiredCodeException|Invalid code provided, please request a code again.)/.test(message),
        message: () => ('This Password reset request has expired, please request a new password reset')
      }
    ]

    this.emailRules = [
      v => !!v || ('Email is required'),
      v => /.+@.+\..+/.test(v) || this._getErrorByName('invalid_email').message()
    ]

    this.passwordRules = [
      v => !!v || ('Password is required'),
      v => (v && v.length >= 8) || this._getErrorByName('password_length').message(),
      v => (v && !!v.match(/(?=.*[a-z])/)) || this._getErrorByName('password_lowercase').message(),
      v => (v && !!v.match(/(?=.*[A-Z])/)) || this._getErrorByName('password_uppercase').message(),
      v => (v && !!v.match(/(?=.*\d)/)) || this._getErrorByName('password_number').message()
    ]

    this.codeRules = [
      v => !!v || ('Please insert your Verification code'),
      v => (v && v.length === 6) || ('The Verification code must be 6 numbers')
    ]

    // working flags
    this.bootstrapping = ref(false)
    this.gettingCognitoUser = ref(false)
    this.signingIn = ref(false)
    this.completingNewPassword = ref(false)
    this.confirmingSignIn = ref(false)
    this.fetchingCmsUser = ref(false)
    this.answeringForgotPasswordCode = ref(false)
    this.sendingForgotPasswordRequest = ref(false)
    this.changingPassword = ref(false)
    this.signingup = ref(false)
    this.confirmingSignup = ref(false)
    this.resendingSignup = ref(false)
    this.signingOut = ref(false)

    this.newPasswordRequired = ref(false)
    this.emailVerificationRequired = ref(false)
    /**
     * Amazon Cognito User
     * @type {import('node_modules/@vue/reactivity/dist/reactivity').ShallowRef<import('node_modules/amazon-cognito-identity-js/src/CognitoUser').default>}
     */
    this.cognitoUser = shallowRef(null)
    /**
     * Temporary Amazon Cognito User, internal use
     * @type {import('node_modules/amazon-cognito-identity-js/src/CognitoUser'.default>}
     */
    this._temporaryCognitoUser = null
    /**
     * Database User
     */
    this.user = ref(null)
    /**
     * @type {import('node_modules/@vue/reactivity/dist/reactivity').Ref<string>}
     */
    this.authError = ref(null)

    this.graphQLClient = null
    this.userQuery = gql`
      query GetUser($slug: String, $id: String, $status: ContentStatus) {
        User: GetUser(slug: $slug, id: $id, status: $status) {
          id
          email
        }
      }
    `

    this.working = computed(() => {
      return this.bootstrapping.value ||
           this.gettingCognitoUser.value ||
           this.signingIn.value ||
           this.completingNewPassword.value ||
           this.confirmingSignIn.value ||
           this.fetchingCmsUser.value ||
           this.answeringForgotPasswordCode.value ||
           this.sendingForgotPasswordRequest.value ||
           this.changingPassword.value ||
           this.signingup.value ||
           this.confirmingSignup.value ||
           this.resendingSignup.value ||
           this.signingOut.value
    })
  }

  /**
   *
   * @param event
   * @param data
   * @returns {Promise<void>}
   * @private
   */
  async _onAuthEvent ({ payload: { event, data } }) {
    // console.log('Auth received event', event, data)
    let error
    switch (event) {
      case 'signUp':
        // data is User
        // this.cognitoUser = data.user
        break
      case 'completeNewPassword_failure':
      case 'forgotPassword_failure':
      case 'forgotPasswordSubmit_failure':
      case 'signUp_failure':
      case 'signIn_failure':
        // data is error message (if string)
        // data is {code, message, name} (if object)
        if (!Object.prototype.hasOwnProperty.call(data, 'code') || (Object.prototype.hasOwnProperty.call(data, 'code') && data.code === 'UserNotConfirmedException')) {
          this.emailVerificationRequired.value = true
        } else {
          error = this._getErrorForServerMessage(data)
          if (error) this.authError.value = error.message()
        }
        break
      case 'signIn':
        await this._setCognitoUser(data)
        break
      case 'signOut':
        // data is signed out User
        this._temporaryCognitoUser = null
        this.cognitoUser.value = null
        this.user.value = null
        break
    }
  }

  /**
   *
   * @param name
   * @returns {*}
   * @private
   */
  _getErrorByName (name) {
    return this.errors.find((err) => err.name === name)
  }

  /**
   *
   * @param serverResponse
   * @returns {{name: string, message: (function())}|null}
   * @private
   */
  _getErrorForServerMessage (serverResponse) {
    let messageToTest = JSON.stringify({ ...serverResponse, message: serverResponse.message })
    if (messageToTest === '{}') {
      messageToTest = serverResponse.code + serverResponse.message
    }
    const found = this.errors.find((err) => err.test(messageToTest))
    return found || (
      this.env.SERVICE_STAGE === 'develop'
        ? {
            name: 'unknown',
            message: () => ('Unknown Error: ') + messageToTest
          }
        : null
    )
  }

  /**
   *
   * @param user
   * @returns {Promise<void>}
   * @private
   */
  async _setCognitoUser (user) {
    this._setChallengeState(user)
    const newUserToken = user?.signInUserSession?.accessToken?.jwtToken
    const oldUserToken = this.cognitoUser.value?.signInUserSession?.accessToken?.jwtToken

    this.cognitoUser.value = user
    this._temporaryCognitoUser = null
    if (user && user.username && newUserToken !== oldUserToken) {
      // console.log('cognito user has changed')
      this.user.value = await this.fetchCmsUser(user.username)
    }
  }

  /**
   *
   * @param user
   * @private
   */
  _setChallengeState (user) {
    if (user && user.challengeName) {
      switch (user.challengeName) {
        case 'NEW_PASSWORD_REQUIRED':
          this._temporaryCognitoUser = user
          this.newPasswordRequired.value = true
          break
        case 'SMS_MFA':
        case 'SOFTWARE_TOKEN_MFA':
        case 'MFA_SETUP':
          // just in case, possible challenges
          break
        default:
          this._temporaryCognitoUser = null
          this.newPasswordRequired.value = false
      }
    }
  }

  /**
   *
   * @returns {Promise<null>}
   */
  async bootstrap (env) {
    this.env = env

    if (this.env.IDENTITY_POOL_ID.indexOf('{ Ref') === 0) {
      throw new Error('Evironment Variables not correctly exported.')
    }

    this.bootstrapping.value = true
    /**
     * configure Amplify auth
     */
    await Auth.configure({
      identityPoolId: this.env.IDENTITY_POOL_ID,
      region: this.env.SERVICE_REGION,
      userPoolId: this.env.USER_POOL_ID,
      userPoolWebClientId: this.env.USER_POOL_CLIENT_ID
      /**
       * this is only needed for google login
       */
      // oauth: {
      //   // domain: `${this.env.SERVICE_NAME}-${this.env.SERVICE_STAGE}.auth.${this.env.SERVICE_REGION}.amazoncognito.com`,
      //   // domain: `${this.env.SERVICE_NAME}-${this.env.SERVICE_STAGE}-auth.monogrid.io`,
      //   domain: `auth-${this.env.SERVICE_STAGE}.if-festival.it`,
      //   redirectSignIn: window.location.href.indexOf('localhost') !== -1 ? 'http://localhost:8080' : this.env.SERVICE_STAGE === 'develop' ? `https://${this.env.SERVICE_TARGET_DOMAIN}` : 'https://italiansfestival.it/live/',
      //   redirectSignOut: window.location.href.indexOf('localhost') !== -1 ? 'http://localhost:8080' : this.env.SERVICE_STAGE === 'develop' ? `https://${this.env.SERVICE_TARGET_DOMAIN}` : 'https://italiansfestival.it/live/',
      //   responseType: 'code',
      //   scope: ['profile', 'email', 'openid'],
      //   options: {
      //     urlOpener: async (url, redirectSignIn) => {
      //       // debugger
      //       const windowProxy = window.open(url, '_top')
      //       if (windowProxy) {
      //         return Promise.resolve(windowProxy)
      //       } else {
      //         return Promise.reject(new Error())
      //       }
      //     }
      //   }
      // }
    })

    try {
      this.anonymousCredentials = null
      await this._setCognitoUser(await Auth.currentAuthenticatedUser())
    } catch (e) {
      this.anonymousCredentials = await Auth.currentCredentials()
      this.cognitoUser.value = null
      this.user.value = null
    }
    this.bootstrapping.value = false
    return this.cognitoUser.value
  }

  /**
   *
   * @returns {Promise<null|any>}
   */
  async getCognitoUser () {
    this.gettingCognitoUser.value = true

    try {
      this.anonymousCredentials = null
      const cognitoUser = await Auth.currentAuthenticatedUser()
      // do not await
      this._setCognitoUser(cognitoUser)
      this.gettingCognitoUser.value = false
      return cognitoUser
    } catch (e) {
      this.anonymousCredentials = await Auth.currentCredentials()
      this.cognitoUser.value = null
      this.user.value = null
    }
    this.gettingCognitoUser.value = false
    return null
  }

  /**
   *
   * @param email
   * @param password
   * @returns {Promise<any>}
   */
  async signIn (email, password) {
    // reset stuff
    this.newPasswordRequired.value = false
    this.emailVerificationRequired.value = false
    this.authError.value = null

    this.signingIn.value = true
    try {
      const user = await Auth.signIn(email, password)
      this._setChallengeState(user)
      this.signingIn.value = false
      return user
    } catch (e) {
      this.signingIn.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   * When user is forced to change password
   * @param user
   * @param newPassword
   * @returns {Promise<any>}
   */
  async completeNewPassword (newPassword) {
    // const { requiredAttributes } = user.challengeParam // the array of required attributes, e.g ['email', 'phone_number']
    this.completingNewPassword.value = true

    try {
      const data = await Auth.completeNewPassword(this._temporaryCognitoUser, newPassword)
      await this._setCognitoUser(data)
      this.newPasswordRequired.value = false
      this.completingNewPassword.value = false
      return data
    } catch (e) {
      this.completingNewPassword.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   * When sign-in has a challenge like mfa
   * @param user
   * @param code
   * @param mfaType
   * @returns {Promise<void>}
   */
  async confirmSignin (user, code, mfaType = 'SMS_MFA') {
    this.confirmingSignIn.value = true

    try {
      // If MFA is enabled, sign-in should be confirmed with the confirmation code
      await this._setCognitoUser(await Auth.confirmSignIn(
        user, // Return object from Auth.signIn()
        code, // Confirmation code
        mfaType // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
      ))
      // const loggedUser = await Auth.confirmSignIn(
      //   user, // Return object from Auth.signIn()
      //   code, // Confirmation code
      //   mfaType // MFA Type e.g. SMS_MFA, SOFTWARE_TOKEN_MFA
      // )
      // Vue.set(this, 'cognitoUser', loggedUser)
      this.confirmingSignIn.value = false
    } catch (e) {
      this.confirmingSignIn.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @param email
   * @param password
   * @param clientMetadata
   * @returns {Promise<ISignUpResult>}
   */
  async signup (email, password, clientMetadata) {
    this.signingup = true
    try {
      const data = await Auth.signUp({
        username: email,
        email,
        password,
        attributes: {
          email
        },
        validationData: [], // optional
        clientMetadata
      })
      this.signingup = false
      return data
    } catch (e) {
      this.signingup = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   * When user is required to verify his email
   * @param email
   * @param code
   * @returns {Promise<any>}
   */
  async confirmSignup (email, code) {
    this.confirmingSignup.value = true

    try {
      // After retrieving the confirmation code from the user
      const data = await Auth.confirmSignUp(email, code, {
        // Optional. Force user confirmation irrespective of existing alias. By default set to True.
        forceAliasCreation: true
      })
      this.confirmingSignup.value = false
      return data
    } catch (e) {
      this.confirmingSignup.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @returns {Promise<void>}
   */
  async signInWithGoogle () {
    Auth.federatedSignIn({ provider: 'Google' })
  }

  /**
   *
   * @param email
   * @returns {Promise<any>}
   */
  async resendSignUp (email) {
    this.resendingSignup.value = true

    try {
      const data = await Auth.resendSignUp(email)
      this.resendingSignup.value = false
      return data
    } catch (e) {
      this.resendingSignup.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @returns {Promise<any>}
   */
  async signOut () {
    this.signingOut.value = true

    try {
      const data = await Auth.signOut()
      this.signingOut.value = false
      // reset stuff
      this.newPasswordRequired.value = false
      this.emailVerificationRequired.value = false
      this.authError.value = null
      return data
    } catch (e) {
      this.signingOut.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @param user
   * @param oldPassword
   * @param newPassword
   * @returns {Promise<"SUCCESS">}
   */
  async changePassword (user, oldPassword, newPassword) {
    this.changingPassword.value = true
    try {
      const data = await Auth.changePassword(user, oldPassword, newPassword)
      this.changingPassword.value = false
      return data
    } catch (e) {
      this.changingPassword.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   * Sends a forgot password request
   * @param email
   * @returns {Promise<any>}
   */
  async sendForgotPasswordRequest (email) {
    this.sendingForgotPasswordRequest.value = true
    try {
      const data = await Auth.forgotPassword(email)
      this.sendingForgotPasswordRequest.value = false
      return data
    } catch (e) {
      this.sendingForgotPasswordRequest.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @param email
   * @param code
   * @param password
   * @returns {Promise<void>}
   */
  async answerForgotPasswordCode (email, code, password) {
    this.answeringForgotPasswordCode.value = true
    try {
      const data = await Auth.forgotPasswordSubmit(email, code, password)
      this.answeringForgotPasswordCode.value = false
      return data
    } catch (e) {
      this.answeringForgotPasswordCode.value = false
      // this.authError.value = e.toString()
      throw e
    }
  }

  /**
   *
   * @param id
   * @returns {Promise<*>}
   */
  async fetchCmsUser (id) {
    this.fetchingCmsUser.value = true
    let result
    try {
      result = await this.graphQLClient.query(this.userQuery, { id, status: 'DRAFT' }, { requestPolicy: 'network-only' }).toPromise()
    } catch (e) {
      // debugger
    }

    this.fetchingCmsUser.value = false
    return result?.data?.User
  }
}

const CMSAuth = new CMSAuthManager()
/**
 *
 */
class CMSAuthPlugin {
  /**
   *
   * @param {import('node_modules/@vue/runtime-core/dist/runtime-core').App} app
   * @param {Object} config

  */
  static install (app, { graphQLClient, userQuery } = {}) {
    CMSAuth.graphQLClient = graphQLClient
    if (userQuery) {
      CMSAuth.userQuery = userQuery
    }
    if (CMSAuth.cognitoUser.value) {
      CMSAuth.fetchCmsUser(CMSAuth.cognitoUser.value.username).then(user => {
        CMSAuth.user.value = user
        return user
      }).catch((error) => console.error(error))
    }

    app.config.globalProperties.$auth = CMSAuth

    setupDevtoolsPlugin(
      {
        id: 'com.monogrid.auth',
        label: 'MONOGRID CMS Auth',
        packageName: 'monogrid-cms-auth',
        homepage: 'https://next.router.vuejs.org/',
        logo: 'https://vuejs.org/images/icons/favicon-96x96.png',
        componentStateTypes: ['Auth'],
        app
      },
      api => {
      // display state added by the plugin
        api.on.inspectComponent((payload, ctx) => {
          if (payload.instanceData) {
            payload.instanceData.state.push({
              type: 'Auth',
              key: '$auth',
              editable: false,
              value: reactive({
                working: CMSAuth.working.value,
                newPasswordRequired: CMSAuth.newPasswordRequired.value,
                emailVerificationRequired: CMSAuth.emailVerificationRequired.value,
                authError: CMSAuth.authError,
                user: CMSAuth.user?.value
              })
            })
          }
        })
      })
  }
}

const PUBLIC_ACL = 0
const REGISTERED_ACL = 1
const CREATOR_ACL = 2
const EDITOR_ACL = 3
const ADMIN_ACL = 4
const NOONE_ACL = 5

/**
 * Given an Access Level
 * It compares a Cognito User permissions with the requested ACL
 *
 * @param {import('node_modules/amazon-cognito-identity-js/src/CognitoUser').default} user the user to check ACL against
 * @param {0|1|2|3|4|5} requestedACL the requested ACL
 * @param {string} contentCreator email of the content creator
 * @returns {boolean} true if the user has the recuested access level, false otherwise
 */
const userHasRequestedACL = (user, requestedACL, contentCreator) => {
  if (isNaN(requestedACL) || requestedACL === null || requestedACL === undefined) {
    throw new Error('You must define a valid ACL to check against')
  }

  if (!user || (user && !user.signInUserSession)) {
    // no user was passed, we let PUBLIC_ACL pass
    return requestedACL === PUBLIC_ACL
  }
  const claims = user.signInUserSession.idToken.payload
  let userACL = PUBLIC_ACL // start from the minumim acl
  // eslint-disable-next-line
  if (claims['cognito:groups'] && claims['cognito:groups'].length > 0 && claims['cognito:groups'].indexOf(ADMINS_GROUP_NAME) !== -1) {
    userACL = ADMIN_ACL
  // eslint-disable-next-line
  } else if (claims['cognito:groups'] && claims['cognito:groups'].length > 0 && claims['cognito:groups'].indexOf(EDITORS_GROUP_NAME) !== -1) {
    userACL = EDITOR_ACL
  } else if (claims.email === contentCreator) {
    userACL = CREATOR_ACL
  } else {
    userACL = REGISTERED_ACL // user has claims but nothing else
  }

  return userACL >= requestedACL
}

export {
  CMSAuthPlugin,
  CMSAuth,
  userHasRequestedACL,
  PUBLIC_ACL,
  REGISTERED_ACL,
  CREATOR_ACL,
  EDITOR_ACL,
  ADMIN_ACL,
  NOONE_ACL
}
